import { useState } from 'react';

const templateCategories = [
  { key: 'Saudação', label: 'Saudação', icon: 'ri-hand-heart-line' },
  { key: 'Promoção', label: 'Promoção', icon: 'ri-megaphone-line' },
  { key: 'Lembrete', label: 'Lembrete', icon: 'ri-calendar-check-line' },
  { key: 'Agradecimento', label: 'Agradecimento', icon: 'ri-hearts-line' },
  { key: 'Confirmação', label: 'Confirmação', icon: 'ri-verified-badge-line' },
  { key: 'Personalizado', label: 'Personalizado', icon: 'ri-paint-brush-line' },
];

const variableGroups = [
  {
    label: 'Contato',
    variables: ['nome', 'telefone', 'email'],
  },
  {
    label: 'Empresa',
    variables: ['empresa', 'cidade', 'estado', 'endereco'],
  },
  {
    label: 'Campanha',
    variables: ['data', 'hora', 'codigo', 'link', 'valor', 'produto', 'plano', 'vencimento'],
  },
];

const previewExamples: Record<string, string> = {
  nome: 'João Silva',
  telefone: '(11) 98765-4321',
  email: 'joao@email.com',
  empresa: 'Acme Corp',
  cidade: 'São Paulo',
  estado: 'SP',
  endereco: 'Av. Paulista, 1000',
  data: '15/01/2025',
  hora: '14:30',
  codigo: 'CUPOM25',
  link: 'https://oferta.exemplo.com',
  valor: 'R$ 199,90',
  produto: 'Smartphone XYZ',
  plano: 'Premium',
  vencimento: '20/01/2025',
};

interface TemplateData {
  id: string;
  name: string;
  content: string;
  category: string;
  is_favorite: boolean;
}

interface TemplateModalProps {
  template: TemplateData | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: { name: string; content: string; category: string; is_favorite: boolean }) => void;
  saving?: boolean;
}

export default function TemplateModal({ template, isOpen, onClose, onSave, saving }: TemplateModalProps) {
  const [name, setName] = useState(template?.name ?? '');
  const [content, setContent] = useState(template?.content ?? '');
  const [category, setCategory] = useState(template?.category ?? 'Personalizado');
  const [isFavorite, setIsFavorite] = useState(template?.is_favorite ?? false);
  const [showPreview, setShowPreview] = useState(true);

  if (!isOpen) return null;

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('template-content') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = content.substring(0, start) + `{{${variable}}}` + content.substring(end);
    setContent(newContent);
    setTimeout(() => {
      textarea.focus();
      const pos = start + `{{${variable}}}`.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleSave = () => {
    if (!name.trim() || !content.trim()) return;
    onSave({
      name: name.trim(),
      content: content.trim(),
      category,
      is_favorite: isFavorite,
    });
    onClose();
  };

  const renderedPreview = content.replace(/\{\{(\w+)\}\}/g, (_match, varName) => {
    return `<span class="text-primary-600 font-semibold">${previewExamples[varName] || varName.toUpperCase()}</span>`;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
              <i className={`text-lg ${template ? 'ri-edit-line text-primary-600' : 'ri-add-line text-primary-600'}`} />
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground-950">
              {template ? 'Editar template' : 'Novo template'}
            </h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Name & Category row */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            <div className="sm:col-span-3">
              <label className="block text-sm font-semibold text-foreground-700 mb-1.5">Nome do template</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="Ex: Boas-vindas ao cliente"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-foreground-700 mb-1.5">Categoria</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-background-200 rounded-xl text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              >
                {templateCategories.map((cat) => (
                  <option key={cat.key} value={cat.key}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Message content */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold text-foreground-700">Conteúdo da mensagem</label>
              <span className="text-[11px] text-foreground-400">{content.length} caracteres</span>
            </div>
            <textarea
              id="template-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={7}
              className="w-full px-3.5 py-3 bg-white border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none font-mono leading-relaxed"
              placeholder="Digite sua mensagem... Use {{nome}}, {{empresa}}, etc."
            />
          </div>

          {/* Variable groups */}
          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-3">Inserir variável</label>
            <div className="space-y-3">
              {variableGroups.map((group) => (
                <div key={group.label}>
                  <p className="text-[11px] font-medium text-foreground-400 uppercase tracking-wide mb-2">{group.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {group.variables.map((variable) => (
                      <button
                        key={variable}
                        type="button"
                        onClick={() => insertVariable(variable)}
                        className="px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-lg text-xs font-medium transition-colors cursor-pointer border border-primary-100 hover:border-primary-200 whitespace-nowrap flex items-center gap-1"
                      >
                        <span className="text-[10px] text-primary-400">{'{{'}</span>
                        {variable}
                        <span className="text-[10px] text-primary-400">{'}}'}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Live preview */}
          <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-background-100 bg-white">
              <div className="flex items-center gap-2">
                <i className="ri-eye-line text-sm text-foreground-400" />
                <span className="text-sm font-medium text-foreground-600">Preview</span>
              </div>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer"
              >
                {showPreview ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            {showPreview && (
              <div className="p-4">
                {content.trim() ? (
                  <div className="bg-white rounded-xl border border-background-100 p-4">
                    {/* WhatsApp bubble mock */}
                    <div className="flex items-start gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-whatsapp-line text-emerald-600 text-sm" />
                      </div>
                      <div className="bg-emerald-50 rounded-2xl rounded-tl-md px-4 py-2.5 max-w-[85%]">
                        <p className="text-sm text-foreground-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderedPreview }} />
                        <p className="text-[10px] text-emerald-400 text-right mt-1">Agora</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-foreground-400 text-center py-6">Digite algo para ver o preview</p>
                )}
              </div>
            )}
          </div>

          {/* Favorite toggle */}
          <label className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-background-50 rounded-xl border border-background-200 cursor-pointer hover:bg-background-100 transition-colors">
            <input
              type="checkbox"
              checked={isFavorite}
              onChange={(e) => setIsFavorite(e.target.checked)}
              className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
            />
            <span className="text-sm text-foreground-700">
              <i className={`mr-1.5 ${isFavorite ? 'ri-star-fill text-amber-500' : 'ri-star-line text-foreground-400'}`} />
              Marcar como favorito
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-background-100">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim() || !content.trim()}
            className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 shadow-sm hover:shadow-md"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin" /> Salvando...</>
            ) : template ? (
              <><i className="ri-check-line" /> Salvar alterações</>
            ) : (
              <><i className="ri-add-line" /> Criar template</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}